import { useState, useEffect } from "react";
import { Check, ShoppingBag, LogIn, Truck, CreditCard, Minus, Plus, Trash2 } from "lucide-react";
import { absoluteShopUrl, computeShippingFee } from "./api";
import { PayuAffordabilityWidget } from "./PayuAffordabilityWidget";

const IN_STATES = [
  "Andhra Pradesh", "Arunachal Pradesh", "Assam", "Bihar", "Chhattisgarh", "Goa", "Gujarat",
  "Haryana", "Himachal Pradesh", "Jharkhand", "Karnataka", "Kerala", "Madhya Pradesh",
  "Maharashtra", "Manipur", "Meghalaya", "Mizoram", "Nagaland", "Odisha", "Punjab",
  "Rajasthan", "Sikkim", "Tamil Nadu", "Telangana", "Tripura", "Uttar Pradesh",
  "Uttarakhand", "West Bengal", "Andaman and Nicobar Islands", "Chandigarh",
  "Dadra and Nagar Haveli and Daman and Diu", "Delhi", "Jammu and Kashmir", "Ladakh",
  "Lakshadweep", "Puducherry",
];

const STEPS = [
  { key: "cart", label: "Cart", icon: ShoppingBag },
  { key: "signin", label: "Sign In", icon: LogIn },
  { key: "shipping", label: "Shipping", icon: Truck },
  { key: "payment", label: "Payment", icon: CreditCard },
];

export function StepIndicator({ current }) {
  const idx = STEPS.findIndex((s) => s.key === current);
  return (
    <div className="flex items-center justify-between mb-12" data-testid="checkout-step-indicator">
      {STEPS.map((s, i) => {
        const done = i < idx;
        const active = i === idx;
        const Icon = s.icon;
        return (
          <div key={s.key} className="flex items-center flex-1 last:flex-none">
            <div className="flex flex-col items-center gap-2" data-testid={`checkout-step-${s.key}`}>
              <div
                className={`w-10 h-10 rounded-full flex items-center justify-center border transition-colors duration-300 ${
                  done ? "bg-gold border-gold text-black" : active ? "border-gold text-gold" : "border-line text-mute"
                }`}
              >
                {done ? <Check className="w-4 h-4" /> : <Icon className="w-4 h-4" />}
              </div>
              <span className={`text-[10.5px] tracking-[0.18em] uppercase ${active || done ? "text-gold" : "text-mute"}`}>
                {s.label}
              </span>
            </div>
            {i < STEPS.length - 1 && (
              <div className={`flex-1 h-px mx-2 mb-6 transition-colors duration-300 ${done ? "bg-gold" : "bg-[var(--gx-border)]"}`} />
            )}
          </div>
        );
      })}
    </div>
  );
}

export function CartReview({ cart, shippingInfo, onContinue }) {
  const shippingFee = computeShippingFee(cart.subtotal, cart.items, shippingInfo);
  return (
    <div data-testid="checkout-cart-step">
      <h2 className="font-display text-2xl mb-6">Review your cart</h2>
      <div className="space-y-4">
        {cart.items.map((item) => (
          <div
            key={item.product_id}
            className="flex gap-4 items-center border border-line rounded-2xl p-4"
            data-testid={`checkout-cart-item-${item.product_id}`}
          >
            <div className="w-16 h-16 rounded-lg overflow-hidden bg-[var(--gx-surface-2)] shrink-0">
              {item.image ? <img src={absoluteShopUrl(item.image)} alt="" className="w-full h-full object-cover" /> : null}
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-medium truncate">{item.name}</p>
              <p className="text-gold text-sm">₹{Number(item.price).toLocaleString("en-IN")}</p>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => cart.updateQty(item.product_id, item.qty - 1)}
                data-testid={`checkout-decrease-${item.product_id}`}
                className="w-7 h-7 rounded-full border border-line flex items-center justify-center hover:text-gold"
              >
                <Minus className="w-3 h-3" />
              </button>
              <span className="w-6 text-center text-sm">{item.qty}</span>
              <button
                onClick={() => cart.updateQty(item.product_id, item.qty + 1)}
                data-testid={`checkout-increase-${item.product_id}`}
                className="w-7 h-7 rounded-full border border-line flex items-center justify-center hover:text-gold"
              >
                <Plus className="w-3 h-3" />
              </button>
              <button
                onClick={() => cart.removeItem(item.product_id)}
                data-testid={`checkout-remove-${item.product_id}`}
                className="ml-2 text-mute hover:text-red-400"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          </div>
        ))}
      </div>
      <div className="mt-8 border-t border-line pt-6 space-y-2">
        <div className="flex items-center justify-between text-sm">
          <span className="text-dim">Subtotal</span>
          <span data-testid="checkout-subtotal">₹{cart.subtotal.toLocaleString("en-IN")}</span>
        </div>
        <div className="flex items-center justify-between text-sm">
          <span className="text-dim">Shipping</span>
          <span data-testid="checkout-shipping-fee">
            {shippingFee > 0 ? `₹${shippingFee.toLocaleString("en-IN")}` : "FREE"}
          </span>
        </div>
        {shippingInfo && shippingFee > 0 && (
          <p className="text-[11px] text-mute" data-testid="checkout-free-shipping-hint">
            Add ₹{(shippingInfo.free_threshold - cart.subtotal).toLocaleString("en-IN")} more for free shipping
          </p>
        )}
        <div className="flex items-center justify-between pt-2">
          <span className="text-dim">Total</span>
          <span className="text-gold font-display text-2xl" data-testid="checkout-cart-total">
            ₹{(cart.subtotal + shippingFee).toLocaleString("en-IN")}
          </span>
        </div>
      </div>
      <button type="button" onClick={onContinue} data-testid="checkout-continue-to-signin" className="btn-gold w-full mt-8">
        Proceed to Checkout
      </button>
    </div>
  );
}

export function SignInStep({ onLogin }) {
  return (
    <div className="text-center py-10" data-testid="checkout-signin-step">
      <LogIn className="w-8 h-8 text-gold mx-auto mb-5" />
      <h2 className="font-display text-2xl mb-3">Sign in to continue</h2>
      <p className="text-dim max-w-[42ch] mx-auto mb-8">
        We use your Google account to securely save your order and shipping details.
      </p>
      <button type="button" onClick={() => onLogin()} data-testid="checkout-google-signin" className="btn-gold mx-auto">
        Continue with Google
      </button>
    </div>
  );
}

export function ShippingStep({ initial, onContinue }) {
  const [form, setForm] = useState({
    full_name: initial?.full_name || "",
    phone: initial?.phone || "",
    line1: initial?.line1 || "",
    line2: initial?.line2 || "",
    pincode: initial?.pincode || "",
    city: initial?.city || "",
    state: initial?.state || "",
  });
  const [cityOptions, setCityOptions] = useState(initial?.city ? [initial.city] : []);
  const [manualCity, setManualCity] = useState(!initial?.city);
  const [pinLookup, setPinLookup] = useState("idle"); // idle | loading | done | error

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    if (form.pincode.length !== 6) return;
    let cancelled = false;
    setPinLookup("loading");
    fetch(`https://api.postalpincode.in/pincode/${form.pincode}`)
      .then((r) => r.json())
      .then((data) => {
        if (cancelled) return;
        const offices = data?.[0]?.PostOffice;
        if (data?.[0]?.Status === "Success" && offices?.length) {
          const names = [...new Set(offices.map((o) => o.Name))];
          setCityOptions(names);
          setManualCity(false);
          setPinLookup("done");
          setForm((f) => ({
            ...f,
            city: names.includes(f.city) ? f.city : names[0],
            state: offices[0].State || f.state,
          }));
        } else {
          setCityOptions([]);
          setManualCity(true);
          setPinLookup("error");
        }
      })
      .catch(() => {
        if (!cancelled) {
          setCityOptions([]);
          setManualCity(true);
          setPinLookup("error");
        }
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.pincode]);

  const submit = (e) => {
    e.preventDefault();
    onContinue(form);
  };

  return (
    <form onSubmit={submit} data-testid="checkout-shipping-step" className="space-y-4">
      <h2 className="font-display text-2xl mb-4">Shipping details</h2>
      <div className="grid sm:grid-cols-2 gap-4">
        <input
          required value={form.full_name} onChange={(e) => set("full_name", e.target.value)}
          placeholder="Full name" data-testid="checkout-field-full-name" className="lux-input"
        />
        <input
          required value={form.phone}
          onChange={(e) => set("phone", e.target.value.replace(/\D/g, "").slice(0, 10))}
          type="tel" inputMode="numeric" pattern="[0-9]{10}" title="Enter a 10-digit mobile number"
          placeholder="10-digit mobile number" data-testid="checkout-field-phone" className="lux-input"
        />
      </div>
      <input
        required value={form.line1} onChange={(e) => set("line1", e.target.value)}
        placeholder="Address line 1" data-testid="checkout-field-line1" className="lux-input"
      />
      <input
        value={form.line2} onChange={(e) => set("line2", e.target.value)}
        placeholder="Address line 2 (optional)" data-testid="checkout-field-line2" className="lux-input"
      />
      <div className="grid sm:grid-cols-3 gap-4">
        <input
          required value={form.pincode}
          onChange={(e) => set("pincode", e.target.value.replace(/\D/g, "").slice(0, 6))}
          type="text" inputMode="numeric" pattern="[0-9]{6}" title="Enter a 6-digit pincode"
          placeholder="Pincode" data-testid="checkout-field-pincode" className="lux-input"
        />
        {manualCity ? (
          <input
            required value={form.city} onChange={(e) => set("city", e.target.value)}
            placeholder="City" data-testid="checkout-field-city" className="lux-input"
          />
        ) : (
          <select required value={form.city} onChange={(e) => set("city", e.target.value)} data-testid="checkout-field-city" className="lux-input">
            <option value="" disabled>
              {pinLookup === "loading" ? "Looking up…" : "City / area"}
            </option>
            {cityOptions.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
        )}
        <select required value={form.state} onChange={(e) => set("state", e.target.value)} data-testid="checkout-field-state" className="lux-input">
          <option value="" disabled>
            State
          </option>
          {IN_STATES.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
      </div>
      {manualCity && pinLookup === "error" && (
        <p className="text-[11px] text-mute" data-testid="checkout-pincode-manual-hint">
          Couldn't auto-detect your city from that pincode — please enter it manually.
        </p>
      )}
      <button type="submit" data-testid="checkout-continue-to-payment" className="btn-gold w-full mt-4">
        Continue to Payment
      </button>
    </form>
  );
}

export function PaymentStep({ cart, shippingAddress, shippingInfo, placing, onPlaceOrder }) {
  const shippingFee = computeShippingFee(cart.subtotal, cart.items, shippingInfo);
  return (
    <div data-testid="checkout-payment-step">
      <h2 className="font-display text-2xl mb-6">Review &amp; pay</h2>
      <div className="border border-line rounded-2xl p-5 mb-5">
        <p className="text-[10.5px] tracking-[0.24em] uppercase text-mute mb-3">Shipping to</p>
        <p className="text-sm">
          {shippingAddress?.full_name} · {shippingAddress?.phone}
        </p>
        <p className="text-dim text-sm mt-1">
          {shippingAddress?.line1}
          {shippingAddress?.line2 ? `, ${shippingAddress.line2}` : ""}, {shippingAddress?.city}, {shippingAddress?.state}{" "}
          {shippingAddress?.pincode}
        </p>
      </div>
      <div className="border border-line rounded-2xl p-5 mb-8 space-y-3">
        {cart.items.map((item) => (
          <div key={item.product_id} className="flex justify-between text-sm">
            <span className="text-dim">
              {item.name} × {item.qty}
            </span>
            <span>₹{(item.price * item.qty).toLocaleString("en-IN")}</span>
          </div>
        ))}
        <div className="flex justify-between text-sm">
          <span className="text-dim">Shipping</span>
          <span data-testid="checkout-payment-shipping-fee">
            {shippingFee > 0 ? `₹${shippingFee.toLocaleString("en-IN")}` : "FREE"}
          </span>
        </div>
        <div className="flex justify-between border-t border-line pt-3 mt-2">
          <span className="text-dim">Total</span>
          <span className="text-gold font-display text-xl" data-testid="checkout-total">
            ₹{(cart.subtotal + shippingFee).toLocaleString("en-IN")}
          </span>
        </div>
      </div>

      <div className="border border-line rounded-2xl p-5 mb-8" data-testid="checkout-payment-methods">
        <p className="text-[10.5px] tracking-[0.24em] uppercase text-mute mb-3">Payment methods</p>
        <PayuAffordabilityWidget amount={(cart.subtotal + shippingFee).toFixed(2)} />
      </div>

      <button
        type="button"
        onClick={onPlaceOrder}
        disabled={placing}
        data-testid="checkout-place-order"
        className="btn-gold w-full disabled:opacity-60"
      >
        {placing ? "Redirecting to secure payment…" : "Place Order & Pay"}
      </button>
      <p className="text-[11px] text-mute text-center mt-4">Payments are securely processed by PayU.</p>
    </div>
  );
}
